import { environmentHttpClient } from "client/Api/httpClient";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { TagSaveModel, TagViewModel } from "../Tags/tags";

export enum UserStatus {
	Active = 1,
	Inactive = 2,
	Deleted = 3
};

export enum UserPronounKind {
	None = 0,
	SheHer = 1,
	HeHim = 2,
	TheyThem = 3
}

export type UserIdentityModel = {
	firstName: string | null;
	lastName: string | null;
	jobTitle: string | null;
	department: string | null;
	officeLocation: string | null;
	avatarId: string | null;
	userId: number;
	status: UserStatus;
	pronouns: UserPronounKind;
};

export type WorkingDaysModel = {
	monday: boolean;
	tuesday: boolean;
	wednesday: boolean;
	thursday: boolean;
	friday: boolean;
	saturday: boolean;
	sunday: boolean;
};

export type UserProfileCustomStringViewModel = {
	displayName: string;
	value: string | null;
};

export type UserProfileModel = {
	firstName: string | null;
	lastName: string | null;
	jobTitle: string | null;
	department: string | null;
	officeLocation: string | null;
	secondaryEmailAddress: string | null;
	phoneNumber: string | null;
	birthday: Date | string | null;
	customString1: OptionalModel<UserProfileCustomStringViewModel>;
	customString2: OptionalModel<UserProfileCustomStringViewModel>;
	customString3: OptionalModel<UserProfileCustomStringViewModel>;
	customString4: OptionalModel<UserProfileCustomStringViewModel>;
	customString5: OptionalModel<UserProfileCustomStringViewModel>;
	upcomingBirthday: Date | string | null;
	avatarId: string | null;
	backgroundImageId: string | null;
	emailAddress: string | null;
	projects: readonly TagViewModel[];
	education: readonly TagViewModel[];
	hobbies: readonly TagViewModel[];
	skills: readonly TagViewModel[];
	aboutMe: string | null;
	workingDays: WorkingDaysModel;
	timeZone: string | null;
	hasTimeZoneAutomaticallySet: boolean;
	pronouns: UserPronounKind;
};

export const enum BlockExtraFieldsEditingReason {
	SyncWithActiveDirectoryIsEnabled = 3
}

type FieldRestriction = {
	isDisabled: boolean;
	isEditable: boolean;
};

export type UserProfileEditRestrictions = {
	fieldsBlockReason?: BlockExtraFieldsEditingReason;

	firstName: FieldRestriction;
	lastName: FieldRestriction;
	phone: FieldRestriction;
	email: FieldRestriction;
	job: FieldRestriction;
	department: FieldRestriction;
	officeLocation: FieldRestriction;
	secondaryEmail: FieldRestriction;
	profileImage: FieldRestriction;
	customString1: FieldRestriction;
	customString2: FieldRestriction;
	customString3: FieldRestriction;
	customString4: FieldRestriction;
	customString5: FieldRestriction;
};

export type UserProfileViewModel = {
	editRestrictions: UserProfileEditRestrictions;
	avatarSelectorOptions: AvatarSelectorOptions;
	backgroundImageSelectorOptions: BackgroundImageSelectorOptions;
	model: UserProfileModel;
};

export type AvatarSelectorOptions = {
	supportedImageTypes: string[];
	maxFileSize: number;
	recommendedWidth: number;
	recommendedHeight: number;
};

export type BackgroundImageSelectorOptions = {
	supportedImageTypes: string[];
	maxFileSize: number;
};

export type SaveUserProfileModel = {
	firstName: string | null;
	lastName: string | null;
	jobTitle: string | null;
	department: string | null;
	officeLocation: string | null;
	secondaryEmailAddress: string | null;
	phoneNumber: string | null;
	birthday: Date | null;
	customString1: OptionalModel<string>;
	customString2: OptionalModel<string>;
	customString3: OptionalModel<string>;
	customString4: OptionalModel<string>;
	customString5: OptionalModel<string>;
	aboutMe: string | null;
	projects: readonly TagSaveModel[];
	education: readonly TagSaveModel[];
	hobbies: readonly TagSaveModel[];
	skills: readonly TagSaveModel[];
	workingDays: WorkingDaysModel;
	timeZone: string | null;
	hasTimeZoneAutomaticallySet: boolean;
	pronouns: UserPronounKind;
};

export type SaveUserProfileImagesModel = {
	avatar: OptionalModel<Base64FileUriUploadModel>;
	backgroundImage: OptionalModel<Base64FileUriUploadModel | null>;
};

export type UpdateUserProfileImagesResponse = {
	newAvatarId: string | null;
	newBackgroundImageId: string | null;
};

export function getUserProfileViewModel(signal?: AbortSignal): Promise<UserProfileViewModel> {
	return environmentHttpClient.get<UserProfileViewModel>(
		"/UiApi/UserProfile",
		undefined,
		{ signal }
	);
}

export function saveUserProfile(
	model: SaveUserProfileModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		"/UiApi/UserProfile",
		model,
		{ signal }
	);
}

export function saveImages(
	model: SaveUserProfileImagesModel,
	signal?: AbortSignal
): Promise<UpdateUserProfileImagesResponse> {
	return environmentHttpClient.put<UpdateUserProfileImagesResponse>(
		"/UiApi/UserProfile/Images",
		model,
		{ signal }
	);
}

export function getUserProfileImageUrl(id: string): string {
	return createUrlRelativeToEnvironment("Profile/Image?id=" + encodeURIComponent(id));
}

export function getUserProfileBackgroundImageUrl(id: string): string {
	return createUrlRelativeToEnvironment("Profile/BackgroundImage?id=" + encodeURIComponent(id));
}

export type SetMicrosoftServicesPasswordModel = {
	password: string;
};

export function checkIfCanApplyPassword(signal?: AbortSignal): Promise<void> {
	return environmentHttpClient.get<void>(
		"/UiApi/UserProfile/ApplyPassword",
		undefined,
		{ signal }
	);
}

export function setMicrosoftServicesPassword(
	model: SetMicrosoftServicesPasswordModel,
	signal?: AbortSignal
): Promise<boolean> {
	return environmentHttpClient.post<boolean>(
		"/UiApi/UserProfile/SetMicrosoftServicesPassword",
		model,
		{ signal }
	);
}

export type SaveUserProfessionalInfoModel = {
	jobTitle: string | null;
	department: string | null;
	officeLocation: string | null;
	projects: readonly TagSaveModel[];
	skills: readonly TagSaveModel[];
	workingDays: WorkingDaysModel;
};

export function saveProfessionalInfoStep(
	formData: SaveUserProfessionalInfoModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		"/UiApi/UserProfile/SaveUserProfessionalInfo",
		formData,
		{ signal }
	);
}

export type SaveUserPersonalInfoModel = {
	avatar: OptionalModel<Base64FileUriUploadModel>;
	firstName: string | null;
	lastName: string | null;
	phoneNumber: string | null;
	birthday: Date | null;
	education: readonly TagSaveModel[];
	hobbies: readonly TagSaveModel[];
	aboutMe: string | null;
	pronouns: UserPronounKind;
};

export type UpdateUserPersonalInfoResponse = {
	newAvatarId: string | null;
};

export function saveUserPersonalInfoStep(
	formData: SaveUserPersonalInfoModel,
	signal?: AbortSignal
): Promise<UpdateUserPersonalInfoResponse> {
	return environmentHttpClient.post<UpdateUserPersonalInfoResponse>(
		"/UiApi/UserProfile/SaveUserPersonalInfo",
		formData,
		{ signal }
	);
}
